import React, {useCallback, useEffect, Suspense, useMemo} from 'react'
import {Form, FormDataContext} from '@modifi/plugin-forms'
import {DangerAlert, Row, SuccessAlert, Col, ButtonLink, PreviousIcon} from '@modifi/ui'
import {
  COMPANY_BANK_STATEMENTS,
  PersonDocumentUploadWithInputContainer,
  SELLER_BANK_ACCOUNT_ACCOUNT_HOLDER,
  SELLER_BANK_ACCOUNT_ACCOUNT_NUMBER,
  SELLER_BANK_ACCOUNT_BANK_CODE,
  SELLER_BANK_ACCOUNT_CURRENCIES,
  SELLER_BANK_ACCOUNT_TRANSFER_TYPE,
  BankAccountNumberInputContainer,
  InputPureWithInputContainer,
  SelectPureWithInputContainer,
  useCountrySpecificTranslationKey,
  RadioGroupComponent,
  InputTooltipInfo,
} from '@modifi/plugin-onboarding'
import {useTranslation} from '@modifi/plugin-i18n'
import {TransferMethod} from '@modifi/enums'
import {CustomerUploadManager} from '@modifi/plugin-documents'
import {generatePath} from '@modifi/router'

import {ROUTE_COMPANY_PROFILE_BANK_DETAILS} from '../../../routes/constants'
import {ConnectedFormFooter} from '../../../plugins/onboarding/components/form-cta-footer'
import {
  FormInputComponentCol,
  FormUploadComponentCol,
  ExistingFilesList,
} from '../../../plugins/onboarding/components'
import {FormRow} from '../../../ui/form'
import IntermediaryBankDetails from './components/intermediary-bank-details'
import {EntityFormData, SelectOption} from '../../../types/common'
import {CompanyUnlockRefresher} from '../../../plugins/onboarding/components/company-unlock-refresher'
import {aFetchSellerBankAccounts} from '../../../data/seller-bank-accounts'
import {aFetchFinancingOptions} from '../../../data/financing-options'
import {aFetchCompanyDocuments} from '../../../data/company-documents'

type SellerBankAccountFormData = {
  [SELLER_BANK_ACCOUNT_ACCOUNT_HOLDER]?: string
  [SELLER_BANK_ACCOUNT_ACCOUNT_NUMBER]?: string
  [SELLER_BANK_ACCOUNT_BANK_CODE]?: string
  [SELLER_BANK_ACCOUNT_TRANSFER_TYPE]?: string
  [SELLER_BANK_ACCOUNT_CURRENCIES]?: string[] | string
}

export type SellerBankAccountProps = {
  isLoading: boolean
  requestPending: boolean
  initialize: () => void
  externalBankAccountId?: string
  formData: EntityFormData<SellerBankAccountFormData>
  availableCurrencies: SelectOption[]
  onSubmit: (changes: Record<string, unknown>, externalBankAccountId?: string) => void
  onDiscard: () => void
  hasError: boolean
  errorMessage?: string
  redirect: (route: string) => void
  isValidExternalBankAccountId: boolean
  pageIsVerified: boolean
  externalErrors?: {
    bankCode?: string
    accountNumber?: string
    accountHolder?: string
  }
  hasInvalidBankData: boolean
}

const SellerBankAccount: React.FC<SellerBankAccountProps> = ({
  isLoading,
  requestPending,
  externalBankAccountId,
  initialize,
  availableCurrencies,
  formData,
  onSubmit,
  onDiscard,
  hasError,
  errorMessage,
  redirect,
  isValidExternalBankAccountId,
  pageIsVerified,
  externalErrors,
  hasInvalidBankData,
}) => {
  const {t} = useTranslation(['charon', 'plugin-forms'])
  const countrySpecificKey = useCountrySpecificTranslationKey()

  useEffect(() => {
    initialize()
  }, [initialize])

  useEffect(() => {
    if (!isValidExternalBankAccountId) {
      redirect(generatePath(ROUTE_COMPANY_PROFILE_BANK_DETAILS))
    }
  }, [redirect, isValidExternalBankAccountId])

  const handleSubmit = useCallback(
    (changes: Record<string, any>) => {
      onSubmit(changes, externalBankAccountId)
    },
    [onSubmit, externalBankAccountId]
  )

  const allowedTransferMethodOptions = useMemo(
    () =>
      Object.values(TransferMethod).map(transferMethod => ({
        label: transferMethod,
        value: transferMethod,
        colProps: {
          auto: true,
        },
      })),
    []
  )

  if (isLoading) {
    return null
  }

  return (
    <CustomerUploadManager>
      <Form
        isLoading={requestPending}
        defaultValues={formData.defaultValues}
        initialValues={formData.initialValues}
        onSubmit={handleSubmit}
        onDiscard={onDiscard}
      >
        {pageIsVerified && (
          <SuccessAlert>
            {t(countrySpecificKey('bank-details-page.fully-verified-alert'))}
          </SuccessAlert>
        )}
        {hasError && errorMessage && <DangerAlert>{errorMessage}</DangerAlert>}
        <Row>
          <Col>
            <ButtonLink
              data-target="legal-rep-summary-page"
              onClick={() => redirect(ROUTE_COMPANY_PROFILE_BANK_DETAILS)}
            >
              <PreviousIcon /> {t('summary-page.back-button.label')}
            </ButtonLink>
          </Col>
        </Row>
        <Row>
          <FormInputComponentCol
            t={t}
            inputComponent={RadioGroupComponent}
            inputName={SELLER_BANK_ACCOUNT_TRANSFER_TYPE}
            options={allowedTransferMethodOptions}
            inputProps={{
              small: true,
              labelAddition: (
                <InputTooltipInfo inputName={SELLER_BANK_ACCOUNT_TRANSFER_TYPE} t={t} />
              ),
            }}
          />
        </Row>
        <FormRow>
          <FormInputComponentCol
            t={t}
            inputComponent={SelectPureWithInputContainer}
            inputName={SELLER_BANK_ACCOUNT_CURRENCIES}
            options={availableCurrencies}
            inputProps={{
              isMulti: true,
              labelAddition: <InputTooltipInfo inputName={SELLER_BANK_ACCOUNT_CURRENCIES} t={t} />,
            }}
          />
          <FormInputComponentCol
            t={t}
            inputComponent={InputPureWithInputContainer}
            inputName={SELLER_BANK_ACCOUNT_ACCOUNT_HOLDER}
            isValid={!externalErrors?.accountNumber}
            errorMessage={externalErrors?.accountNumber}
            inputProps={{
              labelAddition: (
                <InputTooltipInfo inputName={SELLER_BANK_ACCOUNT_ACCOUNT_HOLDER} t={t} />
              ),
            }}
          />
          <FormDataContext.Consumer>
            {({data}) => (
              <FormInputComponentCol
                inputName={SELLER_BANK_ACCOUNT_ACCOUNT_NUMBER}
                inputComponent={BankAccountNumberInputContainer}
                t={t}
                isValid={!externalErrors?.accountNumber}
                errorMessage={externalErrors?.accountNumber}
                inputProps={{
                  isIban: data[SELLER_BANK_ACCOUNT_TRANSFER_TYPE] === TransferMethod.SEPA,
                  labelAddition: (
                    <InputTooltipInfo inputName={SELLER_BANK_ACCOUNT_ACCOUNT_NUMBER} t={t} />
                  ),
                }}
              />
            )}
          </FormDataContext.Consumer>
          <FormInputComponentCol
            t={t}
            inputComponent={InputPureWithInputContainer}
            inputName={SELLER_BANK_ACCOUNT_BANK_CODE}
            isValid={!externalErrors?.bankCode}
            errorMessage={externalErrors?.bankCode}
            inputProps={{
              labelAddition: <InputTooltipInfo inputName={SELLER_BANK_ACCOUNT_BANK_CODE} t={t} />,
            }}
          />
        </FormRow>
        <Suspense fallback={null}>
          <Row>
            <FormUploadComponentCol
              inputName={COMPANY_BANK_STATEMENTS}
              uploadFormComponent={PersonDocumentUploadWithInputContainer}
              t={t}
              inputProps={{
                labelAddition: <InputTooltipInfo inputName={COMPANY_BANK_STATEMENTS} t={t} />,
                components: {
                  existingFilesList: ExistingFilesList,
                },
              }}
            />
          </Row>
        </Suspense>

        <IntermediaryBankDetails
          // @ts-expect-error type not generated properly
          bankAccountId={externalBankAccountId}
          availableCurrencies={availableCurrencies}
        />

        <CompanyUnlockRefresher
          refetch={(): any[] => [
            aFetchSellerBankAccounts(),
            aFetchFinancingOptions(),
            aFetchCompanyDocuments(),
          ]}
          initialValues={formData.initialValues}
          defaultValues={formData.defaultValues}
        />
        <ConnectedFormFooter
          loading={requestPending}
          hasError={hasError}
          disableSubmit={hasInvalidBankData}
        />
      </Form>
    </CustomerUploadManager>
  )
}

export default SellerBankAccount
